// Every staff read and write the admin screens make, in 1 place (docs/data-model.md 10.4), so a screen
// never touches the shared code below it. Everything goes as the staff member, with their own session:
// the database and the functions decide who may do what (2-step login, the role, view as), and this
// file only carries the request and turns any refusal into the words to show.
//
// 3 kinds of call, named exactly as the contract names them:
// - The staff reads and the other hub functions a login may call: api.<database name>(args), e.g.
//   api.staff_people({ search: 'sam', limit: 50 }). Args go by their name with or without the p_
//   (search or p_search). Each answers its rows (an array), or throws an AdminError.
// - The Edge Functions (10.4.1, and the stage 3 and 4 ones staff use): api.<function name, each - as _>
//   (body), e.g. api.add_person({ email, full_name, product_id, source, reason, email_them }). The
//   body is the contract's request exactly, action included where it has one (bulk_grant's 'check'
//   or 'grant'). Leave a key out rather than sending null, unless the contract says null.
// - The direct writes under row level security (10.4.2's table): tick_job, hold_free, release_hold,
//   add_note, update_access, register_line and the rest below. Each checks that a row really changed:
//   an update that row level security quietly skips answers with no rows, which is a refusal too.
//
// A refusal is an AdminError: { code, status, message, body }. message is always the line to show:
// a function's own message, a database refusal's own sentence, or 1 of WORDS (shell/errors.js).
import { supabase } from '../lib/supabase.js'
import { callHubFunction } from '../lib/functions.js'

import { AdminError, WORDS, adminError, wordsFor } from './shell/errors.js'

// The words for refusals, and AdminError: a pure module, so the words can be checked on their own
export { AdminError, WORDS, adminError, wordsFor }

// p_ in front of every arg that hasn't one, and undefined dropped (a database default then applies)
function rpcArgs(args = {}) {
  const out = {}
  for (const [k, v] of Object.entries(args || {})) {
    if (v === undefined) continue
    out[k.startsWith('p_') ? k : `p_${k}`] = v
  }
  return out
}

// A hub function a login may call: its rows, or an AdminError
export async function rpc(name, args) {
  let res
  try {
    res = await supabase.rpc(name, rpcArgs(args))
  } catch (e) {
    throw adminError(e)
  }
  if (res.error) throw adminError(res.error)
  return res.data ?? []
}

// An Edge Function by its exact name: its 200 answer, or an AdminError with its own words
export async function call(name, body = {}) {
  const clean = {}
  for (const [k, v] of Object.entries(body || {})) if (v !== undefined) clean[k] = v
  try {
    return await callHubFunction(name, clean)
  } catch (e) {
    throw adminError(e)
  }
}

// A table in the hub schema, for a screen's own select (row level security decides what comes back)
export const from = table => supabase.from(table)

// A Data API query's rows, or an AdminError: await run(api.from('cohorts').select('id, name'))
export async function run(query) {
  let res
  try {
    res = await query
  } catch (e) {
    throw adminError(e)
  }
  if (res.error) throw adminError(res.error)
  return res.data
}

// An update or delete that must change at least 1 row: row level security answers a refused update
// with no rows rather than an error
async function changed(query) {
  const rows = await run(query)
  if (!rows || (Array.isArray(rows) && rows.length === 0)) throw new AdminError('not_there', 0, WORDS.notThere)
  return rows
}

// ---- The staff reads (10.4.2), by their database names -------------------------------------------

const READS = [
  'staff_people', 'people_outside_hub', 'people_outside_hub_bulk', 'person_history', 'person_progress',
  'today_jobs', 'today_schedule', 'today_new', 'today_going_quiet', 'today_access_ending',
  'resource_open_counts', 'webinar_stats', 'audit_log_page',
  'report_progress_by_cohort', 'report_attendance', 'report_watch_time', 'report_logins', 'report_access_by_status',
  'session_register',
  // referrals (decision 55, migration 79; data-model 10.7): the ledger, and who can refer 1 product
  'staff_introductions', 'referral_candidates',
  // a replay's register page (docs/replay-register-spec.md 3.5; owner and admin): who registered on 1
  // webinar ({ webinar }), how many on each webinar with a link, and 1 person's ({ person })
  'replay_registrations_for', 'webinar_register_counts', 'person_replay_registrations',
]
export const reads = Object.fromEntries(READS.map(name => [name, args => rpc(name, args)]))

// ---- The Edge Functions, by their names with _ for - ----------------------------------------------

const FUNCTIONS = [
  // stage 5 (10.4.1)
  'add-person', 'grant-access', 'bulk-grant', 'revoke-access', 'resend-login', 'send-card-link',
  'reply-question', 'issue-certificate', 'resolve-payment', 'move-access-email', 'merge-people',
  'delete-person', 'data-export',
  // stage 5's review: an owner resets another staff member's 2-step login ({ person_id })
  'reset-two-step',
  // stage 3 and 4 functions staff use: sign-upload (resources and covers), sign-file (covers, files),
  // stripe-prices (amounts, unmapped), log-out-everywhere ({ person_id }), client-space (open, end)
  'sign-upload', 'sign-file', 'stripe-prices', 'log-out-everywhere', 'client-space',
  // stage 6: the clear-ups, asked once as an owner or admin opens admin ({}; shell/tidy.js)
  'admin-tidy',
  // the accountability log: email a member a link to their log ({ person_id, product_id, line })
  'send-log-link',
]
export const functions = Object.fromEntries(FUNCTIONS.map(name => [name.replaceAll('-', '_'), body => call(name, body)]))

// ---- The direct writes and reads beside them (10.4.2) --------------------------------------------

// A person's old addresses, newest first (owner and admin)
export function previous_emails(personId) {
  return run(from('previous_emails').select('id, email, changed_at, changed_by_kind, changed_by')
    .eq('person_id', personId).order('changed_at', { ascending: false }))
}

// Tick a Today job off (done) or back on (open): every id in the job's flag_ids. done_by and done_at
// are the database's.
export function tick_job(flagIds, { done = true, resolution } = {}) {
  const ids = Array.isArray(flagIds) ? flagIds : [flagIds]
  const patch = { status: done ? 'done' : 'open' }
  if (resolution !== undefined) patch.resolution = resolution
  return changed(from('flags').update(patch).in('id', ids).select('id, status, done_at, done_by, resolution'))
}

// Hold a free item back from 1 person, with a reason; release it. held_by and released_by are the
// database's (owner, admin).
export async function hold_free({ person_id, product_id, reason }) {
  if (!String(reason || '').trim()) throw new AdminError('reason_needed', 400, 'Add a reason first. Only staff see it.')
  return (await run(from('free_holds').insert({ person_id, product_id, reason: reason.trim() })
    .select('id, person_id, product_id, reason, held_at')))[0]
}
export function release_hold(holdId) {
  return changed(from('free_holds').update({ released_at: new Date().toISOString() }).eq('id', holdId).is('released_at', null)
    .select('id, released_at'))
}

// A note about someone, only staff see it (owner, admin). The author is the caller.
export async function add_note({ person_id, body, author_id }) {
  const text = String(body || '').trim()
  if (!text) throw new AdminError('empty_note', 400, 'Type a note first.')
  return (await run(from('person_notes').insert({ person_id, body: text, author_id })
    .select('id, person_id, body, author_id, created_at')))[0]
}

// Pause, resume, a new end date, a new intake, a new balance date, 7 more days: 1 access row
// (owner, admin). guard_access decides what may change and stamps who did it; the audit names it.
const ACCESS_FIELDS = ['status', 'paused_until', 'pause_reason', 'ends_at', 'cohort_id', 'balance_due_at', 'suspend_on', 'reason']
export async function update_access(accessId, patch) {
  const bad = Object.keys(patch || {}).filter(k => !ACCESS_FIELDS.includes(k))
  if (bad.length || !Object.keys(patch || {}).length) throw new AdminError('bad_args', 400, WORDS.fallback)
  return (await changed(from('access').update(patch).eq('id', accessId)
    .select('id, status, starts_at, ends_at, ended_at, cohort_id, paused_until, pause_reason, balance_due_at, suspend_on, end_reason')))[0]
}

// A register line: present, absent or excused, with a note (owner, admin, support; a host for their
// product's sessions). source, recorded_by and recorded_at are the database's.
export async function register_line({ session_id, person_id, status, note }) {
  const row = { session_id, person_id, status }
  if (note !== undefined) row.note = note
  return (await run(from('attendance').upsert(row, { onConflict: 'session_id,person_id' })
    .select('session_id, person_id, status, source, note, recorded_by, recorded_at')))[0]
}
export function remove_register_line({ session_id, person_id }) {
  return changed(from('attendance').delete().eq('session_id', session_id).eq('person_id', person_id).select('session_id'))
}

// A staff role, and a host's products (owner only; never their own role)
export async function set_role(personId, role) {
  return (await changed(from('people').update({ role: role || null }).eq('id', personId).select('id, role')))[0]
}
export async function add_scope({ person_id, product_id }) {
  return (await run(from('staff_scopes').insert({ person_id, product_id }).select('person_id, product_id, created_at')))[0]
}
export function remove_scope({ person_id, product_id }) {
  return changed(from('staff_scopes').delete().eq('person_id', person_id).eq('product_id', product_id).select('person_id'))
}

// The products a host is scoped to (a host reads their own rows; owner and admin read everyone's)
export async function staff_scopes(personId) {
  const rows = await run(from('staff_scopes').select('person_id, product_id').eq('person_id', personId))
  return rows.map(r => r.product_id)
}

// The catalogue as admin lists it: paid then free, each in the Library's order (staff at 2-step login
// read drafts too). The Library's own columns (src/library/load.js), so the 2 reads are 1 shape.
export const PRODUCT_COLUMNS = 'id, slug, path, name, tagline, type, access_kind, state, look, moved_in, term_months, sort_order, live_sessions_audience, join_opens_minutes, settings'
export function products() {
  return run(from('catalogue').select(PRODUCT_COLUMNS).order('access_kind', { ascending: false }).order('sort_order'))
}

// View as a member, and stop (owner, admin; an owner only for a member of staff). lib/viewAs.jsx's
// useViewAs() wraps these with what has to happen round them.
export const start_view_as = personId => rpc('start_view_as', { person: personId })
export const stop_view_as = () => rpc('stop_view_as')

// Undo a whole bulk grant (owner, admin)
export const undo_grant_batch = batchId => rpc('undo_grant_batch', { batch: batchId })

export const whoami = async () => (await rpc('whoami'))[0] || null

// Whether switching something on now puts it in members' bells (hub.bell_will_ring, stage 6's batch,
// data-model 10.5), asked before the write that switches it on, so a screen says "it's in their bell"
// only when the database will write the bell items. kind: 'new_recording', 'new_webinar' or
// 'new_resource'; product: the product it's on; item: the recording, webinar or placement, or null for
// 1 not added yet. It writes nothing. A refusal or no answer is false, so no line claims the bell
// when it can't be sure.
export async function bell_will_ring({ kind, product, item = null }) {
  if (!kind || !product) return false
  try {
    return (await rpc('bell_will_ring', { kind, product, item })) === true
  } catch {
    return false
  }
}

// ---- Referrals (decision 55, migration 79; docs/data-model.md 10.7) --------------------------------

// The 10 staff writes, owner's and admin's at 2-step login. Each refusal is 22023 with a code of its own as
// the database's message (no_offer, flag_open, ...), which an AdminError would otherwise lose: it's kept as
// the error's code, so src/admin/referrals/words.js can put it into words (REF.refusals). Any other refusal
// (not_allowed, View as's read-only line, no connection) is adminError's as everywhere else.
const REF_CODE = /^[a-z][a-z_]{2,40}$/
async function refWrite(name, args) {
  let res
  try {
    res = await supabase.rpc(name, rpcArgs(args))
  } catch (e) {
    throw adminError(e)
  }
  if (res.error) {
    const e = adminError(res.error)
    if (res.error.code === '22023' && REF_CODE.test(res.error.message || '')) e.code = res.error.message
    throw e
  }
  return res.data
}
export const set_referral_tick = ({ person, product, ticked }) => refWrite('set_referral_tick', { person, product, ticked })
export const tick_referral_intake = ({ product, cohort }) => refWrite('tick_referral_intake', { product, cohort })
export const undo_referral_tick_batch = ({ batch }) => refWrite('undo_referral_tick_batch', { batch })
export const save_referral_offer = ({ product, offer }) => refWrite('save_referral_offer', { product, offer })
export const add_introduction = ({ referrer, product, name, email, status }) => refWrite('add_introduction', { referrer, product, name, email, status })
export const set_introduction_status = ({ id, status }) => refWrite('set_introduction_status', { id, status })
export const qualify_introduction = ({ id, amount }) => refWrite('qualify_introduction', { id, amount })
export const mark_introduction_paid = ({ id, reference }) => refWrite('mark_introduction_paid', { id, reference })
export const reassign_introduction = ({ id, referrer }) => refWrite('reassign_introduction', { id, referrer })
export const decide_introduction = ({ id, accept, product }) => refWrite('decide_introduction', { id, accept, product })

// A person's codes and ticks, read straight under the policies (owner and admin at 2-step login)
export const REFERRAL_CODE_COLUMNS = 'code, person_id, is_primary, source, created_at'
export const REFERRAL_TICK_COLUMNS = 'person_id, product_id, ticked_at, ticked_by, batch_id'
export function referral_codes(personId) {
  return run(from('referral_codes').select(REFERRAL_CODE_COLUMNS).eq('person_id', personId).order('created_at'))
}
export function referral_ticks(personId) {
  return run(from('referral_ticks').select(REFERRAL_TICK_COLUMNS).eq('person_id', personId))
}
// Every offer (owner and admin), for Offers and the product pickers
export const REFERRAL_OFFER_COLUMNS = 'product_id, intake_link, ref_param, starts_as, fee_kind, fee_value, price_monthly, price_terms, qualify_rule, positioning_line, who_its_for, key_points, how_to_introduce, never_say, is_active, updated_at'
export function referral_offers() {
  return run(from('referral_offers').select(REFERRAL_OFFER_COLUMNS).order('product_id'))
}

// ---- Together ------------------------------------------------------------------------------------

const DIRECT = {
  previous_emails, tick_job, hold_free, release_hold, add_note, update_access, register_line, remove_register_line,
  set_role, add_scope, remove_scope, staff_scopes, products, start_view_as, stop_view_as, undo_grant_batch, whoami,
  bell_will_ring,
  set_referral_tick, tick_referral_intake, undo_referral_tick_batch, save_referral_offer, add_introduction,
  set_introduction_status, qualify_introduction, mark_introduction_paid, reassign_introduction, decide_introduction,
  referral_codes, referral_ticks, referral_offers,
}

// The api every screen is given. With the shell's me, add_note fills in its author.
export function adminApi(me) {
  const actor = me?.who?.person_id || null
  return {
    ...reads,
    ...functions,
    ...DIRECT,
    add_note: args => add_note({ author_id: actor, ...args }),
    rpc, call, from, run, db: supabase, words: wordsFor, AdminError, WORDS,
  }
}

const api = adminApi(null)
export default api
